import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import {
  AdminPage,
  Button,
  ErrorState,
  Panel,
  Skeleton,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { formatILS } from '@/lib/money';
import { errorMessage } from '@/lib/errors';
import { monthKey, useSalesMutations, useTargets } from '../api';

const FIELD = 'min-h-11 w-36 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * יעדי מכירות חודשיים (PRD 5.18.3).
 *
 * יעד לסניף, ואם רוצים גם חלוקה לעובדים. "להעתיק מהחודש הקודם" הוא
 * הכפתור שהכי משתמשים בו: היעד בדרך כלל זהה או קרוב.
 */
export function TargetsPage() {
  const canManage = usePerm('sales.targets');
  const { me } = useMe();
  const [month, setMonth] = useState(monthKey());
  const targets = useTargets(month);
  const mutations = useSalesMutations();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const previousMonth = () => {
    const date = new Date(month);
    date.setMonth(date.getMonth() - 1);
    return monthKey(date);
  };

  return (
    <AdminPage
      title="יעדי מכירות"
      subtitle="יעד חודשי לכל סניף, ואם רוצים גם לכל עובד/ת"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="month"
            value={month.slice(0, 7)}
            onChange={(event) => setMonth(`${event.target.value}-01`)}
            aria-label="חודש"
            className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
          />
          <Button
            onClick={() => {
              setDone(null);
              run(async () => {
                const copied = await mutations.copyTargets.mutateAsync({
                  from: previousMonth(),
                  to: month,
                });
                setDone(
                  copied === 0
                    ? 'אין מה להעתיק: או שאין יעדים בחודש הקודם, או שכולם כבר מוגדרים.'
                    : `הועתקו ${copied} יעדים.`,
                );
              });
            }}
          >
            <Copy size={20} strokeWidth={1.75} aria-hidden="true" />
            להעתיק מהחודש הקודם
          </Button>
        </div>
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="m-0 text-body-sm text-good">
          {done}
        </p>
      )}

      {targets.isPending && <Skeleton className="h-40" />}
      {targets.isError && <ErrorState onRetry={() => void targets.refetch()} />}

      {targets.data?.branches.map((branch) => (
        <Panel key={branch.branch_id} title={branch.name}>
          <div className="grid gap-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="grid gap-1">
                <label
                  htmlFor={`branch-${branch.branch_id}`}
                  className="text-body-sm text-muted"
                >
                  יעד הסניף
                </label>
                <input
                  id={`branch-${branch.branch_id}`}
                  type="number"
                  min={0}
                  defaultValue={branch.target ?? ''}
                  key={`${branch.branch_id}-${month}-${branch.target ?? 'none'}`}
                  onBlur={(event) => {
                    const amount = Number(event.target.value);
                    if (!amount || amount === branch.target || !me?.employeeId) return;
                    run(() =>
                      mutations.setTarget.mutateAsync({
                        month,
                        branch_id: branch.branch_id,
                        employee_id: null,
                        amount,
                        created_by: me.employeeId,
                      }),
                    );
                  }}
                  className={FIELD}
                />
              </div>
              {branch.last_month !== null && (
                <span className="min-h-11 content-center text-body-sm text-muted">
                  בחודש הקודם {formatILS(branch.last_month)}
                </span>
              )}
            </div>

            {branch.employees.length > 0 && (
              <details className="grid gap-2">
                <summary className="cursor-pointer text-body-sm text-muted">
                  יעדים אישיים ({branch.employees.length} עובדים)
                </summary>
                <div className="mt-2 grid gap-2">
                  {branch.employees.map((employee) => (
                    <div key={employee.employee_id} className="flex flex-wrap items-center gap-2">
                      <label
                        htmlFor={`emp-${employee.employee_id}`}
                        className="grow text-body"
                      >
                        {employee.name}
                      </label>
                      <input
                        id={`emp-${employee.employee_id}`}
                        type="number"
                        min={0}
                        defaultValue={employee.target ?? ''}
                        key={`${employee.employee_id}-${month}-${employee.target ?? 'none'}`}
                        onBlur={(event) => {
                          const amount = Number(event.target.value);
                          if (!amount || amount === employee.target || !me?.employeeId) return;
                          run(() =>
                            mutations.setTarget.mutateAsync({
                              month,
                              branch_id: branch.branch_id,
                              employee_id: employee.employee_id,
                              amount,
                              created_by: me.employeeId,
                            }),
                          );
                        }}
                        className={FIELD}
                      />
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </Panel>
      ))}
    </AdminPage>
  );
}
